import { Inject, Injectable, NgZone, Renderer2, RendererFactory2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { LMContextService } from '@services/lm-context.service';
import { environment } from '@environments/environment';
import { Subject } from 'rxjs';

export type LMThemeId =
  'theme-new-light' |
  'theme-new-dark' |
  'theme-new-contrast';

export enum LMColorId {
  default   = 'default',
  alabaster = 'alabaster',
  amber     = 'amber',
  amethyst  = 'amethyst',
  azure     = 'azure',
  emerald   = 'emerald',
  graphite  = 'graphite',
  ruby      = 'ruby',
  slate     = 'slate',
  turquoise = 'turquoise'
}

@Injectable()
export class LMThemeService {
  private static overlayColors = {
    'theme-new-light': '#f0f0f0',
    'theme-new-dark': '#f0f0f0',
    'theme-new-contrast': '#f0f0f0',
  };

  // todo ppatton arguably should go to some other class. Perhaps app-constants?
  private static developmentThemePath = {
    'theme-new-light':    { landmark: '/cli/src/assets/themes/landmark-new-light.css', ids: '/cli/src/assets/soho/css/theme-new-light.css' },
    'theme-new-dark':     { landmark: '/cli/src/assets/themes/landmark-new-dark.css', ids: '/cli/src/assets/soho/css/theme-new-dark.css' },
    'theme-new-contrast': { landmark: '/cli/src/assets/themes/landmark-new-contrast.css', ids: '/cli/src/assets/soho/css/theme-new-contrast.css' }
  };

  // todo ppatton arguably should go to some other class. Perhaps app-constants?
  private static productionThemePath = {
    'theme-new-light':    `/cli/dist/assets/themes/landmark-new-light.css`,
    'theme-new-dark':     `/cli/dist/assets/themes/landmark-new-dark.css`,
    'theme-new-contrast': `/cli/dist/assets/themes/landmark-new-contrast.css`
  };

  public static DEFAULT_THEME: LMThemeId = 'theme-new-light';

  private isProduction = environment.production;
  private renderer: Renderer2;
  private availableThemes: SohoTheme[];

  /** ok to have this state since only one theme is set at a time */
  private currentThemeId: LMThemeId;

  private currentColors: SohoPersonalizationColors;

  /** ok to have this state since only one color is set at a time */
  private currentColorId: LMColorId;

  // subscribe to listen for theme change events
  private themeChangeSubject: Subject<any>;
  private colorChangeSubject: Subject<any>;

  private lastRandomBgColorIndex = 0;
  private lastRandomBgColor: string;

  private foregroundTextMapAlphaLower = new Map();
  private foregroundTextMapNumeric = new Map();

  private sohoIconNames: Array<string>;
  private sohoIconEmptyMessageNames: Array<string>;

  private productionThemePaths;

  constructor(
    rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document,
    private ngZone: NgZone,
    private lmContextService: LMContextService
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.themeChangeSubject = new Subject<string>();
    this.colorChangeSubject = new Subject<string>();

    // build LMThemeInfo array from SohoTheme array
    this.availableThemes = Soho.theme.themes().map(theme => ({
      id: theme.id,
      name: LMThemeService.getAlternateThemeName(theme.id as LMThemeId)
    }));

    this.currentColors = Soho.theme.personalizationColors();

    // remove the default option
    delete this.currentColors.default;

    //remove classic themes from theme dropdown
    this.availableThemes = this.availableThemes.filter(theme => theme.id.indexOf('classic') === -1);

    if (this.isProduction) {
      const paths = LMThemeService.productionThemePath;
      for (const key in paths) {
        if (paths.hasOwnProperty(key)) {
          paths[key] = `${paths[key].split('.css')[0]}_${BuildVersion}.css`;
        }
      }

      this.productionThemePaths = paths;
    }

    /**
     *  Create alphalower foreground text map
     *  Mod: Half of total Alphabet length (break into chunks)
     *  Count: Number of Characters in English Alphabet
     *  StartPosition: Start with lowercase 'a' 97-122 = a-z
     */
    this.createForegroundTextMap(26, 97, this.foregroundTextMapAlphaLower);

    /**
     *  Create numeric foreground text map
     *  Mod: Half of total Alphabet length (break into chunks)
     *  Count: Numbers
     *  StartPosition: Start with zero 48-57 = 0-9
     */
    this.createForegroundTextMap(10, 48, this.foregroundTextMapNumeric);
  }

  private validTheme(themeId: LMThemeId) {
    return this.isUpliftTheme(themeId);
  }

  public isValidSohoIcon(name: string): boolean {
    if (!name) {    // safety check
      return false;
    }
    const sohoIconNames = this.getSohoIconNames();
    return sohoIconNames.includes(name);
  }

  public isValidSohoIconEmptyMessage(name: string): boolean {
    if (!name) {    // safety check
      return false;
    }
    const sohoIconEmptyMessageNames = this.getSohoIconEmptyMessageNames();
    return sohoIconEmptyMessageNames.includes(name);
  }

  public isUpliftTheme(theme: LMThemeId) {
    if (theme === 'theme-new-light'
      ||  theme === 'theme-new-dark'
      ||  theme === 'theme-new-contrast') {
      return true;
    }
    return false;
  }

  public getThemeChangeSubject(): Subject<string> {
    return this.themeChangeSubject;
  }

  public getColorChangeSubject(): Subject<string> {
    return this.colorChangeSubject;
  }

  public getAvailableThemes(): SohoTheme[] {
    return this.availableThemes;
  }

  public getCurrentThemeId(): LMThemeId {
    return this.currentThemeId;
  }

  public getCurrentColorId(): LMColorId {
    return this.currentColorId;
  }

  public getThemeColors(): SohoPersonalizationColors {
    return this.currentColors;
    // return this.colorsByTheme[this.currentThemeId];
  }

  public getDefaultColor(): SohoPersonalizationColor {
    return this.currentThemeId === 'theme-new-dark' ? this.getColorById(LMColorId.slate) :  this.getColorById(LMColorId.alabaster);
  }

  public getCurrentColor(): SohoPersonalizationColor {
    return this.getColorById(this.getCurrentColorId());
  }

  public getColorById(colorId: LMColorId): SohoPersonalizationColor {
    return this.currentColors[colorId];
  }

  public setThemeAndColor(
    themeId: LMThemeId = LMThemeService.DEFAULT_THEME,
    colorId: LMColorId = this.currentThemeId === 'theme-new-dark' ? LMColorId.slate: LMColorId.alabaster,
    useCompactFormLayout: boolean,
  ) {
    this._setTheme(themeId, useCompactFormLayout);
    this.setColor(colorId);
  }

  public setTheme(themeId: LMThemeId = LMThemeService.DEFAULT_THEME, useCompactFormLayout: boolean) {
    this._setTheme(themeId, useCompactFormLayout);
    this.setColor(this.getDefaultColor().id as LMColorId);
  }

  public setColor(colorId: LMColorId) {
    if (!(colorId in LMColorId)) {
      colorId = LMColorId.default;
    }

    if (colorId === LMColorId.default) {
      // calling setColors with resets to default in sohoxi
      colorId = this.currentThemeId === 'theme-new-dark' ?  LMColorId.slate: LMColorId.alabaster;
    } /*else {
      const colorValue = this.getColorById(colorId).value;
      (window as any).Soho.setColors(colorValue);
    }*/

    const colorValue = this.getColorById(colorId).value;
    document.documentElement.style.setProperty('--soho-personalization-color', colorValue);

    (window as any).Soho.setColors(colorValue);

    this.currentColorId = colorId;

    this.getColorChangeSubject().next(colorId);
  }

  public getUniqueBackgroundColorClass(textValue: string): string {
    /**
     * Color class names should match the class names within
     * _landmark-new-common.scss
     */
    const colorClasses = [
      'lm-color-amber-bg', 'lm-color-azure-bg', 'lm-color-amethyst-bg',
      'lm-color-turquoise-bg', 'lm-color-emerald-bg', 'lm-color-ruby-bg',
      'lm-color-slate-bg', 'lm-color-graphite-bg', 'lm-color-amethyst-dark-bg',
      'lm-color-ruby-light-bg', 'lm-color-amber-light-bg', 'lm-color-azure-dark-bg',
      'lm-color-emerald-dark-bg'
    ];

    if (textValue === undefined || textValue === null) {
      return colorClasses[1];
    }

    let key = 0;

    this.foregroundTextMapAlphaLower.forEach((value, index) => {
      let newValue = textValue.trim().toLowerCase();

      // Take just the first letter if there are more than two
      if (textValue.length > 2) {
        newValue = newValue.split('')[0];
      }

      if (value.includes(newValue)) {
        key = index;
      }
    });

    this.foregroundTextMapNumeric.forEach((value, index) => {
      let newValue = textValue.trim().toLowerCase();

      // Take just the first number if there are more than two
      if (textValue.length > 2) {
        newValue = newValue.split('')[0];
      }

      if (value.includes(newValue)) {
        key = index;
      }
    });

    return colorClasses[key];
  }

  public getBackgroundColorClass(): string {
    /**
     * Color class names should match the class names within
     * _landmark-new-common.scss
     */
    const colorClasses = [
      '', 'lm-color-amber-bg', 'lm-color-azure-bg', 'lm-color-amethyst-bg',
      'lm-color-turquoise-bg', 'lm-color-emerald-bg', 'lm-color-ruby-bg',
      'lm-color-slate-bg'
    ];

    // Reset index
    if (this.lastRandomBgColorIndex === 7) {
      this.lastRandomBgColorIndex = 0;
    }

    this.lastRandomBgColorIndex++;
    return colorClasses[this.lastRandomBgColorIndex];
  }

  public getContextFormBackgroundColorClass(color?: string): string {
    const themeName = this.getCurrentThemeId();
    if (!themeName.includes('light')) {
      return '';
    }

    const currentColor = color ? color : this.getCurrentColorId();

    /**
     * Color class names should match the class names within
     * _landmark-new-common.scss
     */
    switch (currentColor) {
      case 'amber':
        return 'lm-color-amber-context-bg';
      case 'amethyst':
        return 'lm-color-amethyst-context-bg';
      case 'azure':
        return 'lm-color-azure-context-bg';
      case 'emerald':
        return 'lm-color-emerald-context-bg';
      case 'graphite':
        return 'lm-color-graphite-context-bg';
      case 'ruby':
        return 'lm-color-ruby-context-bg';
      case 'slate':
        return 'lm-color-slate-context-bg';
      case 'turquoise':
        return 'lm-color-turquoise-context-bg';
      default:
        if (themeName === 'theme-new-light') {
          return '';
        }

        return 'lm-color-azure-context-bg';
    }
  }

  /**
   * Returns a soho/uplift icon given an LPL action icon.
   * @param icon the icon to map to a soho/uplift icon
   */
  public lplIconToSohoXI(icon: LMIcon): string | null {
    switch (icon) {
      case 'add':             return 'add';
      case 'apply':           return 'doc-check';
      case 'approve':         return 'check';
      case 'attach':          return 'attach';
      case 'award':           return 'new';
      case 'create':          return 'new-document';
      case 'calculate':       return 'calculator';
      case 'chart':           return 'line-chart';
      case 'claim':           return 'flag';
      case 'copy':            return 'copy';
      case 'delete':          return 'delete';
      case 'edit':            return 'edit';
      case 'export':          return 'export-2';
      case 'filled star':     return 'star-filled';
      case 'filter':          return 'filter';
      case 'finalize':        return 'check2';
      case 'open':            return 'open-folder';
      case 'outlined star':   return 'star-outlined';
      case 'pdf':             return 'pdf-file';
      case 'preview':         return 'print-preview';
      case 'print':           return 'print';
      case 'process':         return 'process';
      case 'refresh':         return 'refresh';
      case 'release':         return 'invoice-released';
      case 'request':         return 'comment';
      case 'reject':          return 'reject';
      case 'save':            return 'save';
      case 'search':          return 'search';
      case 'send':            return 'send';
      case 'submit':          return 'send-submit';
      case 'helperList':      return 'amend';
      case 'transfer':        return 'send';
      case 'undo':            return 'undo';
      case 'validate':        return 'success';

      // Not in bnf but used for generated actionReferences where this icon is needed.
      case 'sort-ascending':  return 'sort-down';
      case 'sort-descending': return 'sort-up';
      case 'amend':           return 'amend'; // todo where is amend used? - Phillip 9/13/2019
      case 'next':            return 'next-page';
      case 'previous':        return 'previous-page';
      case 'save-new':        return 'save-new';
      case 'saved-search':    return 'print-preview';
      case 'drill':           return 'drill';

      default:
        return null;
    }
  }

  private createForegroundTextMap(
    count: number,
    startPosition: number,
    map: Map<number, Array<string>>
  ): void {
    const mod = 13;

    // Create an Array of Mapped indexes in a non sequential order.
    const indexes = Array.from(Array(mod).keys()).sort((a, b) => (a % 2) - (b % 3));
    let n = 0;

    for (let i = 0, l = count; i < l; i++) {
      const currentSet = map.get(i % mod) || [];
      map.set(i % mod, currentSet.concat(String.fromCharCode(startPosition + i)));
    }

    for (let i = 0, l = count; i < l; i++) {
      // Create an Array of char codes in a non sequential order.
      const charCodes = Array.from(Array(count).keys()).map((c) => startPosition + c).sort((a, b) => (a % i) - (b % i));

      for (let x = 0, ln = count; x < ln; x++) {
        const currentSet = map.get(indexes[n]) || [];
        const oneChar = String.fromCharCode((startPosition + i));
        const twoChar = `${oneChar}${String.fromCharCode(charCodes[x])}`;
        map.set(indexes[n], currentSet.concat(twoChar));

        if (n <= (mod - 1))  {
          n++;
        }

        if (n > (mod - 1)) {
          n = 0;
        }
      }
    }
  }

  private getSohoIconNames(): Array<string> {
    if (this.sohoIconNames === undefined) {
      this.sohoIconNames = [];
      const svgElements = document.getElementsByClassName('svg-icons');
      Array.from(svgElements).forEach((htmlElement: HTMLElement) => {
        const svgElement = htmlElement.querySelectorAll('svg');
        if (svgElement && svgElement[0]) {
          const symbols = svgElement[0].querySelectorAll('symbol');
          Array.from(symbols).forEach((symbol) => {
            const iconName = symbol.getAttribute('id').replace('icon-', '');
            this.sohoIconNames.push(iconName);
          });
        }
      });
    }
    return this.sohoIconNames;
  }

  private getSohoIconEmptyMessageNames(): Array<string> {
    if (this.sohoIconEmptyMessageNames === undefined) {
      this.sohoIconEmptyMessageNames = [];
      const svgElements = document.getElementsByClassName('svg-icons-empty');
      Array.from(svgElements).forEach((htmlElement: HTMLElement) => {
        const svgElement = htmlElement.querySelectorAll('svg');
        if (svgElement && svgElement[0]) {
          const symbols = svgElement[0].querySelectorAll('symbol');
          Array.from(symbols).forEach((symbol) => {
            this.sohoIconEmptyMessageNames.push(symbol.getAttribute('id'));
          });
        }
      });
    }
    return this.sohoIconEmptyMessageNames;
  }

  private updateCSSVariables(themeId: LMThemeId, useCompactFormLayout: boolean) {
    // See _landmark-common.scss
    let properties;

    // TODO: Get this from SOHO
    if (this.isUpliftTheme(themeId)) {
      properties = [
        { key: '--soho-field-height', value: useCompactFormLayout ? '28px' : '38px' },
        { key: '--soho-label-height', value: useCompactFormLayout ? '18px' : '21px' },
        { key: '--soho-field-margin', value: useCompactFormLayout ? '12px' : '20px' },
        { key: '--soho-font-size', value: useCompactFormLayout ? '14px' : '16px' },
        { key: '--soho-field-options-button-height', value: useCompactFormLayout ? '26px' : '36px' },
      ];
    } else {
      properties = [
        { key: '--soho-field-height', value: useCompactFormLayout ? '26px' : '34px' },
        { key: '--soho-label-height', value: '14px' },
        { key: '--soho-field-margin', value: useCompactFormLayout ? '12px' : '20px' },
        { key: '--soho-font-size', value: useCompactFormLayout ? '12px' : '14px' },

        { key: '--soho-field-options-button-height', value: useCompactFormLayout ? '24px' : '32px' },
      ];
    }

    properties.forEach((prop) => {
      document.documentElement.style.setProperty(prop.key, prop.value);
    });
  }

  public getValidThemeName(themeId: LMThemeId): LMThemeId {
    switch (themeId as string) {
      // Theme names given to us by mingle.
      case 'Light':    themeId = 'theme-new-light';  break;
      case 'Dark':     themeId = 'theme-new-dark';  break;
      case 'HighContrast': themeId = 'theme-new-contrast';  break;

      // Legacy theme names to actual theme name
      case 'new':   themeId = 'theme-new-light';  break;

      case 'theme-uplift-light':    themeId = 'theme-new-light';  break;
      case 'theme-uplift-dark':     themeId = 'theme-new-dark';  break;
      case 'theme-uplift-contrast': themeId = 'theme-new-contrast';  break;
    }
    return themeId;
  }

  private _setTheme(themeId: LMThemeId, useCompactFormLayout: boolean) {

    themeId = this.getValidThemeName(themeId);

    // in case we get an invalid theme name set to default
    if (!this.validTheme(themeId)) {
      themeId = 'theme-new-light';
    }

    // set the currentThemeId state
    this.currentThemeId = themeId;

    // Update global css variables.
    this.updateCSSVariables(themeId, useCompactFormLayout);

    // set the theme on the Soho.theme API so that we can get the themes color palette
    Soho.theme.setTheme(themeId);

    // update the colors state
    this.currentColors = Soho.theme.personalizationColors();

    delete this.currentColors.default;

    // set the theme name as a class on the html tag. This will allow us to
    // change certain css by scoping the element to the theme.
    const html: HTMLElement = document.getElementsByTagName('html')[0] as HTMLElement;

    this.renderer.removeClass(html, 'theme-new-light');
    this.renderer.removeClass(html, 'theme-new-dark');
    this.renderer.removeClass(html, 'theme-new-contrast');
    this.renderer.addClass(html, themeId);
    this.updateStyleSheets(themeId);
  }

  private updateStyleSheets(themeId: string): void {
    // Stylesheet is already active
    if (!document.getElementById(`stylesheet-landmark-${themeId}`).hasAttribute('disabled')) {
      return;
    }

    const overlay = document.getElementById( 'lm-page-overlay');
    this.renderer.setStyle(overlay, 'display', 'block');
    this.renderer.setStyle(overlay, 'z-index', '999999');

    // Possible stylesheet ids to look for in the DOM
    // Will update with new theme if found
    const stylesheetIds = [
      `stylesheet-landmark-theme-new-light`,
      `stylesheet-landmark-theme-new-dark`,
      `stylesheet-landmark-theme-new-contrast`,
    ];

    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        stylesheetIds.forEach((stylesheetId) => {
          const id = `stylesheet-landmark-${themeId}`;

          if (stylesheetId.includes(themeId)) {
            // Enable new stylesheet
            document.getElementById(id).removeAttribute('disabled');
          } else {
            // Disable current stylesheet
            document.getElementById(stylesheetId).setAttribute('disabled', 'disabled');
          }
        });


        // Check for flag in CSS to know when it has been loaded
        // Could take a while for stylesheet to update on slower networks
        // Using setInterval to prevent flash of content on slower networks
        // Checks every 1 second if CSS has loaded
        const isCssLoaded = setInterval(() => {
          const styles = window.getComputedStyle(document.body);
          const cssloaded = styles.getPropertyValue('--cssloaded');
          if(cssloaded) {
            clearInterval(isCssLoaded);
            const overlay = document.getElementById( 'lm-page-overlay');
            this.renderer.setStyle(overlay, 'display', 'none');
            this.renderer.setStyle(overlay, 'z-index', '-1');
          }
        }, 1000);
      }, 1);
    });
  }

  public getFontSizeClass(headerNumber: number): string {
    switch (headerNumber) {
      case 1: return 'lm-font-xl';
      case 2: return 'lm-font-lg';
      case 3: return 'lm-font-md';
      case 4: return 'lm-font-sm';
      case 5: return 'lm-font-xs';
    }

    return null;
  }

  public isPersonalized(isContextForm: boolean, busFormSpec: LMBusFormSpec, parentIsModal?) {
    if (!isContextForm || parentIsModal) {
      return false;
    }

    if (!busFormSpec.contextForm.background) {
      return false;
    }

    const background = busFormSpec.contextForm.background;
    return background.useThemeColor || background.useBackgroundImage;
  }

  isCurrentThemeDefault() {
    return this.isDefaultTheme(this.getCurrentThemeId());
  }

  isDefaultTheme(theme: string) {
    return theme === LMThemeService.DEFAULT_THEME;
  }

  public static getAlternateThemeName(themeId: LMThemeId) {
    switch (themeId) {
      case 'theme-new-light': return 'Light';
      case 'theme-new-dark': return 'Dark';
      case 'theme-new-contrast': return 'Contrast';
    }
    return 'Unknown Theme (' + themeId + ')';
  }
}
